/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import layoutConf from "../../config/layoutConf";
import DeleteRecordButton from "../input/DeleteRecordButton";
import MaterialUnitGroupDetail from "./MaterialUnitGroupDetail";
import CreateMaterialUnitGroupButton from "../input/CreateMaterialUnitGroupButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
/**
 * @COMPONENT_NAME MaterialUnitGroup
 * @DESCRIPTION 자재 단위 그룹을 관리하는 화면 컴포넌트
 *
 * @STATE
 *   - {Number} height - 화면 높이
 *   - {Object} schema - 자재 단위 그룹 테이블 스키마 정보
 *   - {Object} grid - 자재 단위 그룹 그리드 객체 참조
 *   - {Object} api - 자재 단위 그룹 API 객체 참조
 *   - {Object} selectedMst - 선택된 자재 단위 그룹 정보
 *
 * @FUNCTIONS
 *   - {Function} handleResize - 화면 크기 변경 시 호출되는 함수
 *   - {Function} notifySuccess - 성공 알림을 표시하는 함수
 *   - {Function} notifyFailure - 실패 알림을 표시하는 함수
 *   - {Function} notifyWarning - 경고 알림을 표시하는 함수
 *   - {Function} onSuccess - 작업 성공 시 호출되는 함수
 *   - {Function} onFailure - 작업 실패 시 호출되는 함수
 *   - {Function} onNotifyWarning - 경고 발생 시 호출되는 함수
 *   - {Function} onApiInitialized - API 초기화 시 호출되는 함수
 *   - {Function} onInitialized - 그리드 초기화 시 호출되는 함수
 */

const MaterialUnitGroup = () => {
    const table = "MaterialUnitGroup";
    const view = "VwMaterialUnitGroup";

    const [height, setHeight] = useState(0);

    const [schema, setSchema] = useState();
    const [grid, setGrid] = useState();
    const [api, setApi] = useState();
    const [selectedMst, setSelectedMst] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid) => {
                setSelectedMst(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            grid.selectionChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });

            grid.itemsSourceChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });
        }
    }, [grid]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="vertical">
                <ReflexElement flex={0.6}>
                    <BasicCrudScreenType
                        headerTitle={t("term.materialUnitGroup")}
                        id={table}
                        table={table}
                        view={view}
                        onInitialized={onInitialized}
                        onApiInitialized={onApiInitialized}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                        <CreateMaterialUnitGroupButton
                                            id={table}
                                            schema={schema}
                                            refresh={api}
                                            onSuccess={onSuccess}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="PATCH">
                                        <UpdateRecordButton
                                            id={table}
                                            schema={schema}
                                            grid={grid}
                                            refresh={api}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            disabled={selectedMst?.length === 0}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                                <BasicCrudScreenActionItemLayout>
                                    <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                        <DeleteRecordButton
                                            id={table}
                                            schema={schema}
                                            refresh={api}
                                            grid={grid}
                                            color="secondary"
                                            disabled={selectedMst?.length === 0}
                                            onSuccess={onSuccess}
                                            onFailure={onFailure}
                                            onPartialFailure={onNotifyWarning}
                                        />
                                    </AccessControl>
                                </BasicCrudScreenActionItemLayout>
                            </>
                        )}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />
                <ReflexElement flex={0.4}>
                    <MaterialUnitGroupDetail selectedMst={selectedMst} />
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};

export default MaterialUnitGroup;
